import { observable, action, makeObservable } from 'mobx';
import { RootStore } from 'stores';
import { IPart, IPartVersion, IRegion, ISubModel, Itrim } from 'types';
import { RouteConfigs } from 'config/routes';
import { MESSAGES, toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';
import {
  getPartsService,
  IgetPartParams,
  IAddPartData,
  addPartService,
  editPartData,
  editPartService,
  deletePartService,
  deletePartData,
  subModelData,
  trimData,
  createSubModelService,
  deleteSubModelData,
  deleteSubModelSerice,
  getPartByIdService,
  updateSubModelService,
  deleteTrimService,
  updateTrimService,
  createTrimService,
  orderTrimService,
  getVersionService,
  setVersionService,
  toggleIsVerifiedOrIsEdgeWrapped
} from 'services/parts';

export type IInitialState = {
  data: {
    parts: IPart[];
    limit: number;
    page: number;
    total: number;
    sortBy?: 'asc' | 'desc' | null;
    sortField?: string;
    editPart: IPart | null;
    subModelModal: IPart | null;
    selectedSubmodel: ISubModel | null;
    showVersionModel: string | null;
    editTrim: string | null;
    selectedTrim: null | {
      id: string;
      submodel_id?: string;
      regions: IRegion[];
      name: string;
    };
    trims: Itrim[];
    versions: IPartVersion[];
  };
  ui: {
    getPartsLoading: boolean;
    addPartsLoading: boolean;
    editPartsLoading: boolean;
    deleteLoading: null | string;
    deleteSubModelLoading: string | null;
    addSubmodelLoading: boolean;
    addTrimLoading: boolean;
    editTrimLoading: boolean;
    getVersionLoading: boolean;
    deleteTrimLoading: boolean | string;
  };
};

export const initialState: IInitialState = {
  data: {
    parts: [],
    limit: 10,
    page: 1,
    total: 0,
    editPart: null,
    subModelModal: null,
    selectedSubmodel: null,
    showVersionModel: null,
    trims: [],
    versions: [],
    selectedTrim: null,
    editTrim: null
  },
  ui: {
    getPartsLoading: false,
    addPartsLoading: false,
    editPartsLoading: false,
    deleteLoading: null,
    deleteSubModelLoading: null,
    addSubmodelLoading: false,
    addTrimLoading: false,
    deleteTrimLoading: false,
    getVersionLoading: false,
    editTrimLoading: false
  }
};

export class PartsStore {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      syncPartsInBackground: action,
      getParts: action,
      addParts: action,
      toogleSwitch: action,
      updatePart: action,
      deletePart: action,
      createSubModel: action,
      updateSubmodel: action,
      deleteSubModel: action,
      setState: action,
      reset: action,
      createTrim: action,
      updateTrim: action,
      deleteTrim: action,
      orderTrim: action,
      getVersions: action,
      setVersion: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
  }

  syncPartsInBackground = async () => {
    const { limit, sortBy, sortField, page } = this.state.data;
    const params = { limit, page, sortBy, sortField };
    this.setState({ ui: { ...this.state.ui, getPartsLoading: true } });
    const { data } = await getPartsService({
      ...params,
      offset: (page - 1) * params.limit
    });
    this.setState({
      data: {
        ...this.state.data,
        parts: data.parts,
        total: data.total,
        ...params
      },
      ui: {
        ...this.state.ui,
        getPartsLoading: false
      }
    });
  };

  getParts = async (params: IgetPartParams, page = 1) => {
    try {
      this.setState({ ui: { ...this.state.ui, getPartsLoading: true } });
      const { limit, sortBy, sortField, search } = params;
      const { data } = await getPartsService({
        ...{ limit, page, sortBy, sortField, search },
        offset: (page - 1) * params.limit
      });

      this.setState({
        data: {
          ...this.state.data,
          parts: data.parts,
          total: data.total,
          page,
          ...params
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getPartsLoading: false } });
    }
  };

  addParts = async (
    partsData: IAddPartData
  ): Promise<boolean | undefined | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, addPartsLoading: true } });
      await addPartService(partsData);
      toast(toastTypeEnum.success, MESSAGES.partAddSuccess);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addPartsLoading: false } });
    }
  };

  toogleSwitch = async (data: {
    bool: boolean;
    key: string;
    id: string;
  }): Promise<boolean | undefined | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, addPartsLoading: true } });
      const result = await toggleIsVerifiedOrIsEdgeWrapped(data);
      const findIndex = this.state.data.parts.findIndex(
        value => value.id === data.id
      );
      if (result && findIndex >= 0) console.log(data.key);

      const keyToUpdate = data.key;
      const valueToUpdate = data.bool;
      //this.state.data.parts[findIndex][data.key] = data.bool;
      const updatedParts = this.state.data.parts.map((part: IPart, index) =>
        index === findIndex
          ? {
              ...part,
              [keyToUpdate]: valueToUpdate
            }
          : part
      );

      this.setState({
        data: {
          ...this.state.data,
          parts: updatedParts
        }
      });

      // this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addPartsLoading: false } });
    }
  };

  updatePart = async (data: editPartData): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, editPartsLoading: true } });
      await editPartService(data);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, editPartsLoading: false } });
    }
  };

  deletePart = async (data: deletePartData): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, deleteLoading: data.id } });
      await deletePartService(data);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteLoading: null } });
    }
  };

  createSubModel = async (data: subModelData): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, addSubmodelLoading: true } });
      await createSubModelService(data);
      const { data: partRes } = await getPartByIdService({ id: data.part_id });
      this.setState({
        data: { ...this.state.data, subModelModal: partRes.partData }
      });
      this.state.data.subModelModal = partRes.partData;
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addSubmodelLoading: false } });
    }
  };
  updateSubmodel = async (data: {
    id: string;
    part_id: string;
  }): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, addSubmodelLoading: true } });
      await updateSubModelService(data);
      const { data: partRes } = await getPartByIdService({ id: data.part_id });
      this.setState({
        data: {
          ...this.state.data,
          subModelModal: partRes.partData,
          selectedSubmodel: null
        }
      });
      this.state.data.subModelModal = partRes.partData;
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addSubmodelLoading: false } });
    }
  };

  deleteSubModel = async (
    data: deleteSubModelData
  ): Promise<boolean | void> => {
    try {
      this.setState({
        ui: { ...this.state.ui, deleteSubModelLoading: data.id }
      });
      await deleteSubModelSerice(data);
      const { data: partRes } = await getPartByIdService({ id: data.part_id });
      this.setState({
        data: { ...this.state.data, subModelModal: partRes.partData }
      });
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.success, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteSubModelLoading: null } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };

  createTrim = async (data: trimData): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, addTrimLoading: true } });
      await createTrimService(data);
      if (this.state.data.subModelModal?.id) {
        const { data: partRes } = await getPartByIdService({
          id: this.state.data.subModelModal.id
        });
        this.setState({
          data: {
            ...this.state.data,
            subModelModal: partRes.partData,
            trims: [],
            selectedTrim: null,
            selectedSubmodel: null,
            editTrim: null
          }
        });
        this.state.data.subModelModal = partRes.partData;
      }
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addTrimLoading: false } });
    }
  };
  updateTrim = async (data: {
    file?: File;
    id?: string;
    name?: string;
    region: string;
  }): Promise<boolean | void> => {
    try {
      this.setState({ ui: { ...this.state.ui, editTrimLoading: true } });
      await updateTrimService(data);
      if (this.state.data.subModelModal?.id) {
        const { data: partRes } = await getPartByIdService({
          id: this.state.data.subModelModal.id
        });
        this.setState({
          data: {
            ...this.state.data,
            subModelModal: partRes.partData,
            trims: [],
            selectedTrim: null,
            selectedSubmodel: null,
            editTrim: null
          }
        });
        this.state.data.subModelModal = partRes.partData;
      }
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, editTrimLoading: false } });
    }
  };

  deleteTrim = async (data: string): Promise<boolean | void> => {
    try {
      this.setState({
        ui: { ...this.state.ui, deleteTrimLoading: true }
      });
      await deleteTrimService(data);
      if (this.state.data.subModelModal?.id) {
        const { data: partRes } = await getPartByIdService({
          id: this.state.data.subModelModal.id
        });
        this.setState({
          data: {
            ...this.state.data,
            subModelModal: partRes.partData,
            trims: [],
            selectedTrim: null,
            selectedSubmodel: null,
            editTrim: null
          }
        });
        this.state.data.subModelModal = partRes.partData;
      }
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.success, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteTrimLoading: false } });
    }
  };

  orderTrim = async (data: {
    id: string;
    index: number;
    type: string;
  }): Promise<boolean | void> => {
    try {
      // this.setState({
      //   ui: { ...this.state.ui, getPartsLoading: true }
      // });
      await orderTrimService(data);
      if (this.state.data.subModelModal?.id) {
        const { data: partRes } = await getPartByIdService({
          id: this.state.data.subModelModal.id
        });
        this.setState({
          data: {
            ...this.state.data,
            subModelModal: partRes.partData
            // trims: [],
            // selectedTrim: null,
            // selectedSubmodel: null,
            // editTrim: null
          }
        });
      }
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.success, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteTrimLoading: false } });
    }
  };

  getVersions = async (
    part_id: string,
    part_key: string
  ): Promise<boolean | void> => {
    try {
      this.setState({
        data: { ...this.state.data, showVersionModel: part_key },
        ui: { ...this.state.ui, getVersionLoading: true }
      });
      const result = await getVersionService(part_id);
      this.setState({
        data: {
          ...this.state.data,
          versions: result?.data || []
        }
      });
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.success, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getVersionLoading: false } });
    }
  };

  setVersion = async ({
    id,
    part_id
  }: {
    id: string;
    part_id: string;
  }): Promise<boolean | void> => {
    try {
      this.setState({
        ui: { ...this.state.ui, getVersionLoading: true }
      });
      await setVersionService({ id, part_id });
      this.syncPartsInBackground();
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.success, message);
    } finally {
      this.setState({
        data: { ...this.state.data, showVersionModel: null, versions: [] },
        ui: { ...this.state.ui, getVersionLoading: true }
      });
    }
  };
}
